import { favorites as favoritesApi, notifications as notificationsApi, store as storeApi, type StorefrontScope } from '../api/endpoints.ts';
import type { NotificationItem, NotificationSettings, StockEvent, StorefrontItem, StoreStatus } from '../api/types.ts';
import { createStore } from '../core/store.ts';
import { currentScope } from './address-book.ts';

/**
 * Избранное: множество идентификаторов, чтобы сердце на плитке рисовалось без запроса.
 *
 * Нажатие применяется сразу и уходит на сервер следом; если сервер отказал, состояние
 * откатывается. Список товаров избранного экран грузит сам: ему нужно наличие по адресу.
 */
export const favoritesStore = createStore<ReadonlySet<string>>(new Set());

export async function loadFavorites(): Promise<void> {
  const response = await favoritesApi.ids().catch(() => null);
  if (response) favoritesStore.set(new Set(response.ids));
}

function withFavorite(productId: string, present: boolean): void {
  favoritesStore.set((ids) => {
    const next = new Set(ids);
    if (present) next.add(productId);
    else next.delete(productId);
    return next;
  });
}

export function toggleFavorite(productId: string): void {
  const wasFavorite = favoritesStore.get().has(productId);
  withFavorite(productId, !wasFavorite);
  const call = wasFavorite ? favoritesApi.remove(productId) : favoritesApi.add(productId);
  call.catch(() => withFavorite(productId, wasFavorite));
}

export function resetFavorites(): void {
  favoritesStore.set(new Set());
}

/**
 * Уведомления под колокольчиком: движение заказов и промо одним списком.
 * Список перечитывается при подключении к ленте событий и по событию `notification`.
 */
export type NotificationsState = {
  items: NotificationItem[];
  unread: number;
  settings: NotificationSettings;
  isLoading: boolean;
};

export const notificationsStore = createStore<NotificationsState>({
  items: [],
  unread: 0,
  settings: { orders: true, promo: true },
  isLoading: false,
});

const patchNotifications = (next: Partial<NotificationsState>) => notificationsStore.set((state) => ({ ...state, ...next }));

export async function refreshNotifications(): Promise<void> {
  patchNotifications({ isLoading: notificationsStore.get().items.length === 0 });
  const page = await notificationsApi.list().catch(() => null);
  patchNotifications(page ? { items: page.items, unread: page.unread, isLoading: false } : { isLoading: false });
}

export async function markAllNotificationsRead(): Promise<void> {
  const state = notificationsStore.get();
  if (state.unread === 0) return;
  const now = new Date().toISOString();
  patchNotifications({ unread: 0, items: state.items.map((item) => (item.readAt ? item : { ...item, readAt: now })) });
  await notificationsApi.markAllRead().catch(() => undefined);
}

export async function loadNotificationSettings(): Promise<void> {
  const settings = await notificationsApi.settings().catch(() => null);
  if (settings) patchNotifications({ settings });
}

/** Два независимых переключателя: отключение рекламы не отключает сообщения о курьере. */
export async function updateNotificationSettings(change: { orders?: boolean; promo?: boolean }): Promise<void> {
  const previous = notificationsStore.get().settings;
  patchNotifications({ settings: { orders: change.orders ?? previous.orders, promo: change.promo ?? previous.promo } });
  try {
    patchNotifications({ settings: await notificationsApi.updateSettings(change) });
  } catch {
    patchNotifications({ settings: previous });
  }
}

export function resetNotifications(): void {
  patchNotifications({ items: [], unread: 0 });
}

/**
 * Часы работы, ETA и принимает ли склад адреса заказы. Приходят с главной, при подключении к
 * ленте событий и событием `store`: момент закрытия виден в корзине без перезапуска.
 */
export const storeInfoStore = createStore<StoreStatus | null>(null);

export function applyStoreStatus(status: StoreStatus): void {
  storeInfoStore.set(status);
}

export async function refreshStoreStatus(scope: StorefrontScope = currentScope()): Promise<void> {
  const status = await storeApi.status(scope).catch(() => null);
  if (status) storeInfoStore.set(status);
}

/** «15–30 min»: обещание по времени из настроек сети. */
export function storeEtaText(status: StoreStatus | null): string | null {
  return status ? `${status.etaMinMinutes}–${status.etaMaxMinutes} min` : null;
}

export const isStorePaused = (status: StoreStatus) => status.demandMode === 'paused';
export const isStoreDelayed = (status: StoreStatus) => status.isOpen && status.demandMode === 'delayed';

/**
 * Часы работы в поясе браузера: сервер шлёт их в поясе магазина, и телефон в другом поясе
 * иначе прочёл бы «открываемся в 00:00» в 00:28.
 */
export function localClock(hhmm: string, timezone: string): string {
  const [hours, minutes] = hhmm.split(':').map(Number);
  if (hours === undefined || minutes === undefined || Number.isNaN(hours) || Number.isNaN(minutes)) return hhmm;
  try {
    const now = new Date();
    // Смещение пояса магазина относительно пояса браузера в минутах на текущий момент.
    const inStore = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const inBrowser = new Date(now.toLocaleString('en-US'));
    const shift = Math.round((inBrowser.getTime() - inStore.getTime()) / 60_000);
    if (shift === 0) return hhmm;
    const total = (((hours * 60 + minutes + shift) % 1440) + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  } catch {
    return hhmm;
  }
}

/**
 * Последнее событие остатка для экранов: список и карточка правят по нему свой товар.
 * Событие приходит, только когда остаток мал или вернулся, поэтому закончившийся товар
 * пропадает с полки у всех сразу.
 */
export const stockStore = createStore<{ last: StockEvent | null; version: number }>({ last: null, version: 0 });

export function publishStock(event: StockEvent): void {
  stockStore.set((state) => ({ last: event, version: state.version + 1 }));
}

/** Список с остатком из события; закончившийся товар уходит с полки. Без правок — тот же массив. */
export function applyStockTo(items: StorefrontItem[], event: StockEvent): StorefrontItem[] {
  if (!items.some((item) => item.id === event.productId)) return items;
  return items
    .filter((item) => item.id !== event.productId || event.availableQty > 0)
    .map((item) => (item.id === event.productId ? { ...item, availableQty: event.availableQty } : item));
}
